import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import * as mediaStore from '../api/mediaStore';
import { Link } from 'react-router-dom';

function Filmylist() {
  const { role } = useAuth();
  const isAdmin = role === 'Majitel' || role === 'Admin';

  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false); // disabled UI
  const [modalMode] = useState('add');
  const [editIndex] = useState(null);
  const [form] = useState({ title: '', img: '', link: '' });

  // images picker states
  const [imagesPickerOpen, setImagesPickerOpen] = useState(false);
  const [imagesList, setImagesList] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // unique ids per component instance so labels reliably focus inputs
  const idSuffix = React.useRef(Math.random().toString(36).slice(2, 8));
  const titleInputId = `title-${idSuffix.current}`;
  const imgInputId = `img-${idSuffix.current}`;
  const linkInputId = `link-${idSuffix.current}`;
  const titleRef = React.useRef(null);

  // context modal state for right-click actions (centered modal)
  const [contextModal, setContextModal] = useState({ open: false, idx: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, idx: null });

  // focus title when modal opens
  React.useEffect(() => {
    if (modalOpen) {
      // small delay ensures modal is in DOM
      setTimeout(() => titleRef.current?.focus(), 0);
    }
  }, [modalOpen]);

  useEffect(() => {
    let mounted = true;
    const loadFilms = async () => {
      try {
        // Prefer live server to avoid stale localStorage divergence
        const r = await fetch('/api/popups', { cache: 'no-store' });
        if (r.ok) {
          const all = await r.json();
          const films = (Array.isArray(all) ? all : []).filter(p => String(p.contentType||'') === 'film');
          const mapped = films.map(p => ({ id: String(p.id), title: p.title || 'Film', img: p.listThumb || p.heroImage || '', link: `/filmy?preview=${encodeURIComponent(String(p.id))}` }));
          if (mounted) setItems(mapped);
          try { await mediaStore.setItems('film', mapped); } catch {}
          return;
        }
        // fallback to local cache
        const s = await mediaStore.getItems('film');
        if (mounted) setItems(Array.isArray(s) ? s : []);
      } catch {
        const s = await mediaStore.getItems('film');
        if (mounted) setItems(Array.isArray(s) ? s : []);
      }
    };
    loadFilms();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onCatalog = async () => {
      try { const s = await mediaStore.getItems('film'); setItems(s || []); } catch {}
    };
    window.addEventListener('catalog:changed', onCatalog);
    const onDeleted = async () => { try { const s = await mediaStore.getItems('film'); setItems(s || []); } catch {} };
    window.addEventListener('popup:deleted', onDeleted);
    return () => { window.removeEventListener('catalog:changed', onCatalog); window.removeEventListener('popup:deleted', onDeleted); };
  }, []);

  const openContextFor = () => {};

  const closeContext = () => setContextModal({ open: false, idx: null });

  // provede smazání bez dalšího browser confirmu (potvrzení řeší confirmDelete modal)
  const handleContextDelete = async () => {};

  const handleContextEdit = () => {};

  const openAddModal = () => {};

  const openEditModal = () => {};

  const closeModal = () => {};

  const submitModal = async () => {};

  const onContext = (e, idx) => {
    e.preventDefault();
    openContextFor(idx);
  };

  const openImagesPicker = async () => {
    setImagesPickerOpen(true);
    await loadImages();
  };
  const closeImagesPicker = () => setImagesPickerOpen(false);

  async function loadImages() {
    if (imagesList.length) return;
    setLoadingImages(true);
    try {
      // preferred: /images/Nahledovky/list.json (array of filenames)
      const res = await fetch('/images/Nahledovky/list.json');
      if (res.ok) {
        const arr = await res.json();
        setImagesList(arr.map(p => `/images/Nahledovky/${p}`));
        setLoadingImages(false);
        return;
      }
    } catch (e) { /* ignore */ }

    // fallback probe (limited) - tries common names: thumb-1.jpg, img1.png, 1.jpg ...
    const found = [];
    const prefixes = ['', 'thumb-', 'img-', 'thumb_', 'img_'];
    const exts = ['jpg', 'png', 'webp', 'jpeg'];
    outer: for (let i = 1; i <= 30; i++) {
      for (const pre of prefixes) {
        for (const ex of exts) {
          const url = `/images/Nahledovky/${pre}${i}.${ex}`;
          try {
            // HEAD is lighter, some servers may not support HEAD -> fallback to GET would be heavier
            const r = await fetch(url, { method: 'HEAD' });
            if (r.ok) {
              found.push(url);
              if (found.length >= 50) break outer;
            }
          } catch (err) { /* ignore */ }
        }
      }
    }
    setImagesList(found);
    setLoadingImages(false);
  }

  const selectImage = (url) => {
    setForm(prev => ({ ...prev, img: url }));
    closeImagesPicker();
  };

  return (
    <Container>
      <h4 className="serialy">Filmy</h4>
      <Content>
        {items.map((it) => (
          <Card key={it.id}>
            <Wrap>
              {it.link && it.link.includes('preview=') ? (
                <div
                  className="prevent-select"
                  role="button"
                  tabIndex={0}
                  aria-label={it.title || 'item'}
                  onClick={() => {
                    try {
                      const url = new URL(it.link, window.location.href);
                      window.history.pushState({}, '', url.pathname + url.search);
                      const id = url.searchParams.get('preview');
                      window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } }));
                    } catch (_) {}
                  }}
                >
                  <img className="hovimg" src={it.img || '/images/placeholder-thumb.png'} alt={it.title} />
                </div>
              ) : it.link ? (
                <Link className="prevent-select" to={it.link} aria-label={it.title || 'item'}>
                  <img className="hovimg" src={it.img || '/images/placeholder-thumb.png'} alt={it.title} />
                </Link>
              ) : (
                <div className="prevent-select" role="button" tabIndex={0} aria-label={it.title || 'item'}>
                  <img className="hovimg" src={it.img || '/images/placeholder-thumb.png'} alt={it.title} />
                </div>
              )}
            </Wrap>

            <Meta>
              <Title>{it.title}</Title>
              {/* ...other meta... */}
            </Meta>
          </Card>
        ))}

        {/* Žádná možnost přidání zde – používá se pouze Popup Editor */}
      </Content>

      {modalOpen && (
        <Overlay onClick={closeModal}>
          <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <ModalHeader>{modalMode === 'add' ? 'Přidat film' : 'Upravit film'}</ModalHeader>
            <Form onSubmit={submitModal}>
              <Label
                htmlFor={titleInputId}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={() => document.getElementById(titleInputId)?.focus()}
              >
                Název (povinné)
              </Label>
              <Input
                id={titleInputId}
                ref={titleRef}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Název filmu"
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
              />

              <Label
                htmlFor={imgInputId}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={() => document.getElementById(imgInputId)?.focus()}
              >
                URL obrázku
              </Label>
              <Input
                id={imgInputId}
                value={form.img}
                onChange={(e) => setForm({ ...form, img: e.target.value })}
                placeholder="https://... nebo /images/... "
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && (form.img || '').trim().toLowerCase() === 'images') {
                    e.preventDefault();
                    await openImagesPicker();
                  }
                }}
              />

              <Label
                htmlFor={linkInputId}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={() => document.getElementById(linkInputId)?.focus()}
              >
                Cílová URL / cesta
              </Label>
              <Input
                id={linkInputId}
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="/film-x nebo https://..."
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
              />
              <PreviewRow>
                <PreviewImg src={form.img || '/images/placeholder-thumb.png'} alt="preview" />
                <PreviewText>{form.link || 'Bez odkazu'}</PreviewText>
              </PreviewRow>

              <Buttons>
                <Btn type="button" variant="ghost" onClick={closeModal}>Zrušit</Btn>
                <Btn type="submit" variant="primary">{modalMode === 'add' ? 'Přidat' : 'Uložit'}</Btn>
              </Buttons>
            </Form>
          </Modal>
        </Overlay>
      )}

      {/* Context modal (centered) */}
      {contextModal.open && (
        <Overlay onClick={closeContext}>
          <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <ModalHeader style={{ position: 'relative', textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#cfdff6' }}>Vyber akci pro:</div>
              <div style={{ fontWeight: 700, marginTop: 6 }}>{items[contextModal.idx]?.title}</div>
              <ContextClose onClick={closeContext} aria-label="Zavřít">✕</ContextClose>
            </ModalHeader>

            <ActionRow>
              <Btn type="button" variant="primary" onClick={() => handleContextEdit(contextModal.idx)}>Upravit</Btn>
              <Btn type="button" variant="ghost" onClick={() => setConfirmDelete({ open: true, idx: contextModal.idx })}>Smazat</Btn>
            </ActionRow>
          </Modal>
        </Overlay>
      )}

      {/* in-app confirm delete modal (centered, not window.confirm) */}
      {confirmDelete.open && (
        <Overlay onClick={() => setConfirmDelete({ open: false, idx: null })}>
          <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <ModalHeader style={{textAlign:'center'}}>Potvrdit smazání</ModalHeader>
            <div style={{padding:6, color:'#cfdff6'}}>Opravdu smazat: <strong>{items[confirmDelete.idx]?.title}</strong>?</div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8, marginTop:12}}>
              <Btn type="button" variant="ghost" onClick={() => setConfirmDelete({ open: false, idx: null })}>Zrušit</Btn>
              <Btn type="button" variant="primary" onClick={async () => { await handleContextDelete(confirmDelete.idx); setConfirmDelete({ open: false, idx: null }); }}>Smazat</Btn>
            </div>
          </Modal>
        </Overlay>
      )}

      {/* Images picker (opened when user types "images" and hits Enter) */}
      {imagesPickerOpen && (
        <Overlay onClick={closeImagesPicker}>
          <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{maxWidth:'900px', width:'90%'}}>
            <ModalHeader style={{ position: 'relative', textAlign: 'center' }}>
              Vyber náhled
              <ContextClose onClick={closeImagesPicker} aria-label="Zavřít">✕</ContextClose>
            </ModalHeader>

            {loadingImages ? (
              <div style={{padding:20, textAlign:'center', color:'#cfdff6'}}>Načítám...</div>
            ) : (
              <ImagesGrid>
                {imagesList.length ? imagesList.map((url) => (
                  <Thumb key={url} type="button" onClick={() => selectImage(url)}>
                    <img src={url} alt="" />
                  </Thumb>
                )) : (
                  <div style={{padding:20, color:'#cfdff6'}}>Nebyl nalezen žádný náhled v /images/Nahledovky</div>
                )}
              </ImagesGrid>
            )}
          </Modal>
        </Overlay>
      )}
    </Container>
  );
}

export default Filmylist;

/* styled components below (ke zlepšení vzhledu popupu a vstupů) */
const ContextClose = styled.button`
  position: absolute;
  right: 10px;
  top: 6px;
  background: transparent;
  border: none;
  color: #e6eef8;
  font-size: 16px;
  cursor: pointer;
`;

const Container = styled.div`
  font-size: 25px;
  padding: 0 42px;

  h4 {
    font-family: 'Noto Sans Lao', sans-serif;
    margin-bottom: 10px;
  }
`;

const Content = styled.div`
  display: grid;
  grid-gap: 20px;

  /* cílově 5 dlaždic v řádku na širších obrazovkách */
  grid-template-columns: repeat(5, minmax(180px, 1fr));

  /* postupné zmenšování sloupců při menších šířkách */
  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, minmax(160px, 1fr));
  }
  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 420px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;

// Obal karty pro meta overlay; jednoduchý wrapper, efekty řeší Wrap
const Card = styled.div`
  position: relative;
`;

const Wrap = styled.div`
  /* stejné chování jako Watchlist: 16:9 plocha + cover obrázek, bez natahování */
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 */
  border-radius: 4px;
  overflow: hidden;
  outline: 0px solid transparent;
  background: linear-gradient(rgb(48, 50, 62), rgb(30, 31, 42));
  box-shadow: rgb(0 0 0 / 69%) 0px 26px 30px -10px, rgb(0 0 0 / 73%) 0px 16px 10px -10px;
  cursor: pointer;
  transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s;
  transform-origin: center center;
  will-change: transform, box-shadow;

  img {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover; /* důležité: nedeformovat */
    opacity: 1;
    z-index: 1;
    top: 0;
    -webkit-backface-visibility: visible;
    backface-visibility: visible;
    transform: none !important;
    transition: filter 180ms ease, opacity 180ms ease;
  }

  &:hover {
    outline: 3.2px solid #C7C7C7;
    box-shadow: rgb(0 0 0 / 80%) 0px 40px 58px -16px, rgb(0 0 0 / 72%) 0px 30px 22px -10px;
    transform: scale(1.05);
    transition: all 0.3s ease-in-out;
    -webkit-transition: all 0.3s ease-in-out;
  }
`;

/* upravené "Přidat" tlačítko: stejná velikost jako ostatní položky */
const WrapAdd = styled(Wrap)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgb(48, 50, 62);
  color: white;
  &:hover {
    background: rgb(60, 62, 74);
  }
`;

const AddPlus = styled.span`
  font-size: 30px;
  line-height: 1;
`;

const AddLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  margin-top: 6px;
`;

/* Modal styled */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10,10,12,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const Modal = styled.div`
  width: 420px;
  max-width: calc(100% - 40px);
  background: linear-gradient(180deg,#0f1720,#11131a);
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(2,6,23,0.6);
  padding: 18px;
  color: #e6eef8;
  border: 1px solid rgba(255,255,255,0.04);
`;

const ModalHeader = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 12px;
  opacity: 0.85;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
  color: #e6eef8;
  outline: none;
  font-size: 14px;
  box-sizing: border-box;

  /* jemnější fokus (bez silného modrého glow) */
  &:focus {
    box-shadow: none;
    border-color: rgba(255,255,255,0.12);
    background: rgba(255,255,255,0.02);
  }

  /* odstranit modrý autofill background v Chrome */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-internal-autofill-selected {
    -webkit-text-fill-color: #e6eef8 !important;
    -webkit-box-shadow: 0 0 0px 1000px rgba(255,255,255,0.02) inset !important;
    box-shadow: 0 0 0px 1000px rgba(255,255,255,0.02) inset !important;
  }
`;

const PreviewRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 6px;
`;

const PreviewImg = styled.img`
  width: 72px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.04);
`;

const PreviewText = styled.div`
  font-size: 13px;
  opacity: 0.8;
  color: #cfdff6;
  word-break: break-all;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
`;

const Btn = styled.button`
  min-width: 96px;
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  ${(p) => p.variant === 'primary' ? `
    background: linear-gradient(90deg,#1e90ff,#007bff);
    color: white;
  ` : `
    background: transparent;
    color: #cfdff6;
    border: 1px solid rgba(255,255,255,0.06);
  `}
`;

const Meta = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
`;

const Title = styled.span`
  display: none; /* titulky skryté přes náhled */
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const ImagesGrid = styled.div`
  max-height: 60vh;
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  padding: 10px;
`;

const Thumb = styled.button`
  appearance: none;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  height: 96px;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  margin-top: 12px;
`;

